import React, { useState, useMemo } from 'react';
import Header from '@/components/shared/Header';
import BottomNav from '@/components/shared/BottomNav';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import PlannedItem from './PlannedItem';
import AddPlannedForm from './AddPlannedForm';
import PullToRefresh from '@/components/shared/PullToRefresh';
import { usePlannedExpenses } from '@/hooks/usePlannedExpenses';
import { formatCurrency } from '@/utils/formatters';
import { USERS } from '@/constants';
import { useAppContext } from '@/context/AppContext';

export default function PlannedExpenses() {
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const year = String(new Date().getFullYear());
  const { plannedLabels } = useAppContext();

  // Firestore-level filter: label
  const [filterLabel, setFilterLabel] = useState('');

  // Client-side filters
  const [search, setSearch]       = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');
  const [filterUser, setFilterUser] = useState('');

  // Sort
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir]     = useState('desc');

  // Selection mode
  const [selectMode, setSelectMode]   = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const { planned, loading, yearTotal, addPlanned, updatePlanned, deletePlanned, refetch } =
    usePlannedExpenses(filterLabel ? { label: filterLabel, year } : { year });

  // Client-side: search + amount range + date range + sort
  const displayedPlanned = useMemo(() => {
    let list = [...planned];

    if (search) {
      const term = search.toLowerCase();
      list = list.filter(p =>
        (p.vendor ?? '').toLowerCase().includes(term) ||
        (p.description ?? '').toLowerCase().includes(term)
      );
    }

    const min = minAmount !== '' ? Number(minAmount) : null;
    const max = maxAmount !== '' ? Number(maxAmount) : null;
    if (min !== null && !isNaN(min)) list = list.filter(p => p.amount >= min);
    if (max !== null && !isNaN(max)) list = list.filter(p => p.amount <= max);

    if (dateFrom) list = list.filter(p => p.date >= dateFrom);
    if (dateTo)   list = list.filter(p => p.date <= dateTo);

    if (filterUser) list = list.filter(p => p.createdBy === filterUser);

    list.sort((a, b) => {
      const cmp = sortField === 'amount'
        ? a.amount - b.amount
        : a.date.localeCompare(b.date);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [planned, search, minAmount, maxAmount, dateFrom, dateTo, sortField, sortDir]);

  const filteredTotal = useMemo(
    () => displayedPlanned.reduce((s, p) => s + p.amount, 0),
    [displayedPlanned]
  );

  const sliderMax = useMemo(
    () => Math.max(50000, ...planned.map(p => p.amount)),
    [planned]
  );

  function handleDownload() {
    const rows = [
      ['Date', 'Vendor', 'Description', 'Label', 'Amount (₹)'],
      ...displayedPlanned.map(p => [p.date, p.vendor ?? '', p.description ?? '', p.label ?? '', p.amount]),
    ];
    const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: `NammaPlanned_${year}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selectedIds.size === displayedPlanned.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedPlanned.map(p => p.id)));
    }
  }

  async function handleDeleteSelected() {
    if (!selectedIds.size) return;
    if (!confirm(`Delete ${selectedIds.size} selected item${selectedIds.size !== 1 ? 's' : ''}?`)) return;
    for (const id of selectedIds) {
      await deletePlanned(id);
    }
    setSelectedIds(new Set());
    setSelectMode(false);
  }

  function handleDownloadSelected() {
    const toExport = displayedPlanned.filter(p => selectedIds.has(p.id));
    if (!toExport.length) return;
    const rows = [
      ['Date', 'Vendor', 'Description', 'Label', 'Amount (₹)'],
      ...toExport.map(p => [p.date, p.vendor ?? '', p.description ?? '', p.label ?? '', p.amount]),
    ];
    const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = Object.assign(document.createElement('a'), { href: url, download: `NammaPlanned_${year}_selected.csv` });
    a.click();
    URL.revokeObjectURL(url);
  }

  const rightActions = (
    <button onClick={() => setShowAdd(true)} className="text-white text-xl font-bold" aria-label="Add planned expense">＋</button>
  );

  return (
    <div className="flex flex-col h-full">
      <Header
        title="📦 Planned Expenses"
        bgColor="bg-warning"
        subtitle="Out-of-budget · Not in monthly totals"
        rightActions={rightActions}
      />

      {/* Filter bar */}
      <div className="bg-white border-b border-gray-100 px-3 py-2 space-y-2 overflow-y-auto max-h-72">

        {/* Search */}
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search vendor / description…"
          className="input-field w-full py-1.5 text-xs" aria-label="Search planned expenses" />

        {/* Date range */}
        <div className="flex gap-2">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="input-field flex-1 py-1.5 text-xs" aria-label="From date" />
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="input-field flex-1 py-1.5 text-xs" aria-label="To date" />
        </div>

        {/* Amount range */}
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-500">
            <span>Amount Range (₹)</span>
            <span>{minAmount || 0} – {maxAmount || sliderMax}</span>
          </div>
          <input type="range" min={0} max={sliderMax} step={500}
            value={minAmount !== '' ? minAmount : 0}
            onChange={e => setMinAmount(e.target.value)}
            className="w-full accent-orange-600" aria-label="Minimum amount slider" />
          <input type="range" min={0} max={sliderMax} step={500}
            value={maxAmount !== '' ? maxAmount : sliderMax}
            onChange={e => setMaxAmount(e.target.value)}
            className="w-full accent-orange-600" aria-label="Maximum amount slider" />
          <div className="flex gap-2">
            <input type="number" value={minAmount} min={0} onChange={e => setMinAmount(e.target.value)}
              placeholder="Min ₹" className="input-field flex-1 py-1 text-xs" aria-label="Minimum amount" />
            <input type="number" value={maxAmount} min={0} onChange={e => setMaxAmount(e.target.value)}
              placeholder="Max ₹" className="input-field flex-1 py-1 text-xs" aria-label="Maximum amount" />
          </div>
        </div>

        {/* Sort */}
        <div className="flex gap-2 items-center">
          <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide flex-shrink-0">Sort:</span>
          <select value={sortField} onChange={e => setSortField(e.target.value)}
            className="text-xs border border-gray-200 rounded-input px-2 py-1 bg-white flex-1">
            <option value="date">Date</option>
            <option value="amount">Amount</option>
          </select>
          <button onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
            className="text-xs border border-gray-200 rounded px-2 py-1 bg-white whitespace-nowrap"
            aria-label={`Toggle sort direction, currently ${sortDir}`}>
            {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
          </button>
        </div>

        {/* User filter */}
        <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
          className="text-xs border border-gray-200 rounded-input px-2 py-1 bg-white w-full" aria-label="Filter by user">
          <option value="">All Users</option>
          {Object.values(USERS).map(u => (
            <option key={u.email} value={u.email}>{u.name}</option>
          ))}
        </select>
      </div>

      <PullToRefresh onRefresh={refetch} className="flex-1 overflow-y-auto scrollbar-none pb-20">
        {/* Year total card */}
        <div className="m-3 rounded-card p-4 text-white"
          style={{ background: 'linear-gradient(135deg, #E65100, #BF360C)' }}>
          <p className="text-xs uppercase tracking-wide opacity-80">{year} Total Planned</p>
          <p className="text-3xl font-bold mt-1">{formatCurrency(yearTotal)}</p>
          <p className="text-xs opacity-70 mt-0.5">
            {planned.length} item{planned.length !== 1 ? 's' : ''} · Not counted in monthly budget
          </p>
        </div>

        {/* Label filter chips */}
        <div className="flex gap-2 px-3 pb-2 overflow-x-auto scrollbar-none">
          <button onClick={() => setFilterLabel('')}
            className={`chip ${!filterLabel ? 'chip-selected' : ''}`}>All</button>
          {plannedLabels.map(l => (
            <button key={l.id}
              onClick={() => setFilterLabel(filterLabel === l.id ? '' : l.id)}
              className={`chip ${filterLabel === l.id ? 'chip-selected' : ''}`}>
              {l.label}
            </button>
          ))}
        </div>

        {/* Results summary + download */}
        <div className="mx-3 mb-2 flex items-center justify-between bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSelectMode(m => !m); setSelectedIds(new Set()); }}
              className="text-xs text-orange-700 font-semibold"
              aria-label={selectMode ? 'Exit selection mode' : 'Enter selection mode'}
            >
              {selectMode ? '✕ Cancel' : '☑ Select'}
            </button>
            {selectMode && (
              <>
                <button onClick={toggleSelectAll} className="text-xs text-orange-700 font-semibold">
                  {selectedIds.size === displayedPlanned.length ? 'Deselect All' : 'All'}
                </button>
                <button onClick={handleDownloadSelected} disabled={!selectedIds.size} className="text-xs text-orange-700 font-semibold disabled:opacity-40">⬇ ({selectedIds.size})</button>
                <button onClick={handleDeleteSelected} disabled={!selectedIds.size} className="text-xs text-red-600 font-semibold disabled:opacity-40">🗑 ({selectedIds.size})</button>
              </>
            )}
          </div>
          {!selectMode && (
            <div className="flex items-center gap-3">
              <span className="text-xs text-orange-700 font-semibold">{displayedPlanned.length} items · {formatCurrency(filteredTotal)}</span>
              <button onClick={handleDownload} className="text-xs text-orange-700 font-semibold" aria-label="Download CSV" title="Download as CSV">⬇ CSV</button>
            </div>
          )}
          {selectMode && (
            <span className="text-xs text-orange-700 font-semibold">{displayedPlanned.length} items · {formatCurrency(filteredTotal)}</span>
          )}
        </div>

        {loading ? <LoadingSpinner /> : (
          <div className="px-3 space-y-3">
            {displayedPlanned.length ? (
              displayedPlanned.map(p => (
                <PlannedItem key={p.id} planned={p} onDelete={deletePlanned} onEdit={setEditItem} selectable={selectMode} selected={selectedIds.has(p.id)} onSelect={toggleSelect} />
              ))
            ) : (
              <div className="text-center py-12 text-gray-400">
                <p className="text-3xl mb-2">📦</p>
                <p className="text-sm">No planned expenses found</p>
                <p className="text-xs mt-1">Tap ＋ to add school fees, travel, big purchases…</p>
              </div>
            )}
          </div>
        )}
      </PullToRefresh>

      {showAdd && (
        <AddPlannedForm
          onSave={async (data) => { await addPlanned(data); setShowAdd(false); }}
          onClose={() => setShowAdd(false)}
        />
      )}

      {editItem && (
        <AddPlannedForm
          initialData={editItem}
          onSave={async (data) => { await updatePlanned(editItem.id, data); setEditItem(null); }}
          onClose={() => setEditItem(null)}
        />
      )}

      {/* FAB */}
      <button className="fab" onClick={() => setShowAdd(true)} aria-label="Add planned expense">＋</button>

      <BottomNav />
    </div>
  );
}

